import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Mail, Phone, Clock, Send, LoaderCircle } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
    "AI & Machine Learning",
    "Cloud Transformation",
    "Cybersecurity",
    "System Integration",
    "Data Engineering & Analytics",
    "Application Development",
    "Other",
];

const INFO = [
    { icon: MapPin, label: "Office", value: "Bengaluru India" },
    { icon: Mail, label: "Email", value: "info@synteratechnologies.com" },
    { icon: Phone, label: "Phone", value: "+91 99083 33515" },
    { icon: Clock, label: "Hours", value: "Mon – Sat, 9:30 AM – 6:30 PM IST" },
];

const initialForm = { name: "", email: "", company: "", service: SERVICES[0], message: "" };

export const Contact = () => {
    const [form, setForm] = useState(initialForm);
    const [sending, setSending] = useState(false);

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            await axios.post(`${API}/enquiries`, form);
            toast.success("Enquiry sent. Our team will reach out within one business day.");
            setForm(initialForm);
        } catch (err) {
            toast.error("Could not send your enquiry. Please try again.");
        } finally {
            setSending(false);
        }
    };

    const inputCls =
        "w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3.5 text-sm text-white placeholder:text-slate-500 outline-none transition-all duration-300 focus:border-volt/60 focus:bg-white/[0.06] focus:shadow-[0_0_20px_rgba(0,240,255,0.08)]";

    return (
        <section id="contact" data-testid="contact-section" className="relative py-24 sm:py-32">
            <div className="mx-auto grid max-w-7xl gap-14 px-5 sm:px-8 lg:grid-cols-[1fr_1.1fr]">
                <div>
                    <Reveal>
                        <Eyebrow>Contact Us</Eyebrow>
                        <h2 className="mb-4 font-syne text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl" data-testid="contact-heading">
                            Discover solutions that scale with you
                        </h2>
                        <p className="text-base leading-relaxed text-slate-400 sm:text-lg">
                            Tell us where you want to go. We'll map the fastest, safest route
                            to get there, usually within one business day.
                        </p>
                    </Reveal>

                    <div className="mt-10 space-y-5">
                        {INFO.map((item, i) => (
                            <Reveal key={item.label} delay={0.1 + i * 0.08}>
                                <div className="flex items-start gap-4" data-testid={`contact-info-${item.label.toLowerCase()}`}>
                                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-volt/20 bg-volt/10 text-volt">
                                        <item.icon size={18} />
                                    </span>
                                    <div>
                                        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
                                            {item.label}
                                        </div>
                                        <div className="mt-1 text-sm text-slate-200 sm:text-base">{item.value}</div>
                                    </div>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>

                <Reveal delay={0.15}>
                    <form
                        onSubmit={submit}
                        data-testid="contact-form"
                        className="rounded-3xl border border-white/[0.08] bg-card2/50 p-7 sm:p-9"
                    >
                        <div className="grid gap-5 sm:grid-cols-2">
                            <div>
                                <label htmlFor="contact-name" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">
                                    Name *
                                </label>
                                <input id="contact-name" data-testid="contact-name-input" required value={form.name} onChange={set("name")} placeholder="Your full name" className={inputCls} />
                            </div>
                            <div>
                                <label htmlFor="contact-email" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">
                                    Email *
                                </label>
                                <input id="contact-email" data-testid="contact-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="you@company.com" className={inputCls} />
                            </div>
                            <div>
                                <label htmlFor="contact-company" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">
                                    Company
                                </label>
                                <input id="contact-company" data-testid="contact-company-input" value={form.company} onChange={set("company")} placeholder="Company name" className={inputCls} />
                            </div>
                            <div>
                                <label htmlFor="contact-service" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">
                                    Service Interest
                                </label>
                                <select id="contact-service" data-testid="contact-service-select" value={form.service} onChange={set("service")} className={`${inputCls} appearance-none`}>
                                    {SERVICES.map((s) => (
                                        <option key={s} value={s} className="bg-[#0D111A]">
                                            {s}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="mt-5">
                            <label htmlFor="contact-message" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">
                                Message *
                            </label>
                            <textarea id="contact-message" data-testid="contact-message-input" required rows={5} value={form.message} onChange={set("message")} placeholder="Tell us about your project or challenge..." className={`${inputCls} resize-none`} />
                        </div>
                        <button
                            type="submit"
                            data-testid="contact-submit-btn"
                            disabled={sending}
                            className="group mt-7 flex w-full items-center justify-center gap-2.5 rounded-full bg-volt px-7 py-4 font-mono text-xs font-bold uppercase tracking-[0.15em] text-[#07090E] transition-all duration-300 hover:shadow-[0_0_40px_rgba(0,240,255,0.45)] disabled:opacity-60"
                        >
                            {sending ? <LoaderCircle size={15} className="animate-spin" /> : <Send size={15} className="transition-transform duration-300 group-hover:translate-x-1" />}
                            {sending ? "Sending..." : "Send Enquiry"}
                        </button>
                        <p className="mt-4 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                            Response within one business day
                        </p>
                    </form>
                </Reveal>
            </div>
        </section>
    );
};
